<script setup>
import { ref, computed, onMounted, onUnmounted, defineEmits } from 'vue';
import Bell from '@/icons/bell.vue';
import SoundOn from '@/icons/sound-on.vue';
import SoundOff from '@/icons/sound-off.vue';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/use-notifications';

const router = useRouter();

const emit = defineEmits(['notificationsToggled']);

const { 
  notifications,
  unreadCount,
  isLoading,
  getNotificationTemplate, 
  toggleSound, 
  soundEnabled, 
  initialize,
  fetchNotifications,
  markAsRead,
  deleteNotification,
  onNewNotificationReceived,
  offNewNotificationReceived,
} = useNotifications();

const showNotifications = ref(false);
const isClosing = ref(false);
const notificationContainer = ref(null);
const shouldAnimateBell = ref(false);

let cleanup = null;

const triggerBellAnimation = () => {
  shouldAnimateBell.value = true;
  setTimeout(() => {
    shouldAnimateBell.value = false;
  }, 4000);
};

const closeNotifications = () => {
  isClosing.value = true;
  setTimeout(() => {
    showNotifications.value = false;
    isClosing.value = false;
    emit('notificationsToggled', false);
  }, 200);
};

const toggleNotificationsShow = () => {
  showNotifications.value = !showNotifications.value;
  emit('notificationsToggled', showNotifications.value);
}

const handleNotificationClick = async (notification) => {
  if (!notification.readed) {
    await markAsRead([notification.id]);
  }
  if (notification.link) {
    router.push(notification.link);
  }
  closeNotifications();
};

const handleClickOutside = (event) => {
    if (notificationContainer.value && !notificationContainer.value.contains(event.target)) {
        closeNotifications();
        deleteAllConfirmation.value = false;
    }
};

const handleMarkAllAsRead = async () => {
    const unreadNotificationIds = notifications.value
        .filter(notification => !notification.readed)
        .map(notification => notification.id);
    
    if (unreadNotificationIds.length > 0) {
        await markAsRead(unreadNotificationIds);
    }
};

const deleteAllConfirmation = ref(false);

const handleDeleteAllNotifications = async () => {
    await deleteNotification(notifications.value.map(notification => notification.id));
    deleteAllConfirmation.value = false;
};

onMounted(async () => {
    document.addEventListener('click', handleClickOutside);
    Notification.requestPermission()
    cleanup = initialize();

    onNewNotificationReceived(triggerBellAnimation);
    
    await fetchNotifications(true);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    offNewNotificationReceived(triggerBellAnimation);
    if (cleanup) {
      cleanup();
    }
});

const notificationsNotReaded = computed(() => {
    if (!notifications.value || !Array.isArray(notifications.value)) return [];
    return notifications.value
      .filter(notification => !notification.readed)
      .map(notification => ({ 
        ...notification, 
        ...getNotificationTemplate(notification) 
      }));
});

const notificationsReaded = computed(() => {
    if (!notifications.value || !Array.isArray(notifications.value)) return [];
    return notifications.value
      .filter(notification => notification.readed)
      .map(notification => ({ 
        ...notification, 
        ...getNotificationTemplate(notification) 
      }));
});

const getUnreadCount = () => {
    return unreadCount.value > 99 ? 99 : unreadCount.value;
}
</script>

<template>
    <div ref="notificationContainer" class="relative">
        <div class="flex items-center gap-2">
            <div class="bg-echoes-background-gray hover:bg-echoes-background-gray/80 rounded-md p-1.5 cursor-pointer" @click="toggleNotificationsShow">
                <div class="absolute -right-1 -top-1 md:-right-1 bg-red-500 text-white text-xs rounded-full w-2 h-2 md:w-3 md:h-3 flex items-center justify-center p-1" v-if="getUnreadCount() > 0">
                </div>
                <Bell :class="['w-5 h-5 ext-echoes-gray/50 md:dark:text-amber-500', { 'bell': shouldAnimateBell }]" />
            </div>
        </div>
        
        <div class="absolute end-0 m-0 mt-2 z-10 w-[calc(100vw-36px)] md:w-64 rounded-md overflow-hidden bg-black dark:shadow-gray-800 border border-echoes-border-gray p-2 text-xs" 
             :class="isClosing ? 'dropdown-animate-out' : 'dropdown-animate-in'" 
             v-if="showNotifications">
          
          <div class="bg-black w-full h-full" v-if="deleteAllConfirmation">
            <div class="flex justify-center items-center h-full"> 
              <div class="p-4 rounded-md opacity-100"> 
                <div class="text-sm text-gray-500 dark:text-gray-400">Are you sure you want to delete all notifications?</div>
                <div class="flex justify-between pt-2">
                  <button class="bg-slate-600/50 text-gray-100 py-2 px-4 rounded-[13px] font-semibold transition-colors hover:bg-slate-800 hover:text-white" @click.stop="deleteAllConfirmation = false">Cancel</button>
                  <button class="bg-echoes-primary text-gray-100 py-2 px-4 rounded-[13px] font-semibold transition-colors hover:bg-echoes-primary/80 hover:text-white" @click.stop="handleDeleteAllNotifications">Delete</button>
                </div>
              </div>
            </div>
          </div>

          <div v-else>
            <div class="flex justify-between items-center">
                <div class="text-sm font-semibold text-gray-900 dark:text-white">Notifications</div>
                <div class="cursor-pointer" @click.stop="toggleSound">
                    <SoundOn v-if="soundEnabled" class="w-5 h-5 text-echoes-primary/80 hover:text-echoes-primary" />
                    <SoundOff v-else class="w-5 h-5 text-echoes-primary/80 hover:text-echoes-primary" />
                </div>
            </div>

            <div v-if="isLoading" class="text-center py-4 text-gray-500 dark:text-gray-400">
              Loading notifications...
            </div>

            <div v-else-if="!notifications || !Array.isArray(notifications) || notifications.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
              No notifications
            </div>

            <ul v-else class="py-2 text-start divide-y divide-gray-100 dark:divide-gray-800 max-h-[calc(100vh-200px)] md:max-h-[200px] overflow-y-auto custom-scrollbar-y pe-2 notifications-animate-in">
                <!-- Unread notifications -->
                <li class="text-wrap bg-echoes-background-gray p-1 rounded-md mb-1 relative cursor-pointer hover:bg-echoes-background-gray/80 transition-colors duration-300 text-[12px]" 
                    v-for="notification in notificationsNotReaded" 
                    :key="notification.id" 
                    @click="handleNotificationClick(notification)">
                    <div class="flex flex-col justify-between gap-1">
                        <div class="text-gray-500 dark:text-gray-400 text-[10px] font-bold"><span class="truncate max-w-[50%]">{{notification.title}}</span></div>
                        <div v-html="notification.message"></div>
                      </div>
                      <div class="text-gray-500 dark:text-gray-400 text-[8px] text-start font-semibold mt-1"><span class="truncate max-w-[50%]">{{notification.created_at}}</span></div>
                </li>

                <!-- Read notifications -->
                <li class="text-wrap bg-echoes-background-gray p-1 rounded-md mb-1 opacity-50 cursor-pointer hover:opacity-70" 
                    v-for="notification in notificationsReaded" 
                    :key="notification.id"
                    @click="handleNotificationClick(notification)">
                    <div class="flex flex-col justify-between gap-1">
                      <div class="text-gray-500 dark:text-gray-400 text-[10px] font-bold"><span class="truncate max-w-[50%]">{{notification.title}}</span></div>
                      <div v-html="notification.message"></div>
                    </div>
                    <div class="text-gray-500 dark:text-gray-400 text-[8px] text-start font-semibold mt-1"><span class="truncate max-w-[50%]">{{notification.created_at}}</span></div>
                </li>
            </ul>

            <div class="text-xs font-semibold text-gray-900 dark:text-white flex justify-between mt-2" v-if="notifications && Array.isArray(notifications) && notifications.length > 0">
                <div class="cursor-pointer text-echoes-primary/80 hover:text-echoes-primary" 
                    @click.stop="handleMarkAllAsRead">
                  Mark all as read
                </div>
                <div class="cursor-pointer text-echoes-primary/80 hover:text-echoes-primary" @click.stop="deleteAllConfirmation = true">
                  Delete all
                </div>
            </div>

          </div>
        </div>
    </div>
</template>

<style scoped>
.custom-scrollbar-y::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar-y::-webkit-scrollbar-track {
    background: #232323;
    border-radius: 3px;
}

.custom-scrollbar-y::-webkit-scrollbar-thumb {
    background-color: #FE3E01;
    border-radius: 3px;
}
.bell{
  -webkit-animation: ring 4s ease-in-out;
  -webkit-transform-origin: 50% 4px;
  -moz-animation: ring 4s ease-in-out;
  -moz-transform-origin: 50% 4px;
  animation: ring 4s ease-in-out;
  transform-origin: 50% 4px;
}

@-webkit-keyframes ring {
  0% { -webkit-transform: rotateZ(0); }
  1% { -webkit-transform: rotateZ(30deg); }
  3% { -webkit-transform: rotateZ(-28deg); }
  5% { -webkit-transform: rotateZ(34deg); }
  7% { -webkit-transform: rotateZ(-32deg); }
  9% { -webkit-transform: rotateZ(30deg); }
  11% { -webkit-transform: rotateZ(-28deg); }
  13% { -webkit-transform: rotateZ(26deg); }
  15% { -webkit-transform: rotateZ(-24deg); }
  17% { -webkit-transform: rotateZ(22deg); }
  19% { -webkit-transform: rotateZ(-20deg); }
  21% { -webkit-transform: rotateZ(18deg); }
  23% { -webkit-transform: rotateZ(-16deg); }
  25% { -webkit-transform: rotateZ(14deg); }
  27% { -webkit-transform: rotateZ(-12deg); }
  29% { -webkit-transform: rotateZ(10deg); }
  31% { -webkit-transform: rotateZ(-8deg); }
  33% { -webkit-transform: rotateZ(6deg); }
  35% { -webkit-transform: rotateZ(-4deg); }
  37% { -webkit-transform: rotateZ(2deg); }
  39% { -webkit-transform: rotateZ(-1deg); }
  41% { -webkit-transform: rotateZ(1deg); }

  43% { -webkit-transform: rotateZ(0); }
  100% { -webkit-transform: rotateZ(0); }
}

@-moz-keyframes ring {
  0% { -moz-transform: rotate(0); }
  1% { -moz-transform: rotate(30deg); }
  3% { -moz-transform: rotate(-28deg); }
  5% { -moz-transform: rotate(34deg); }
  7% { -moz-transform: rotate(-32deg); }
  9% { -moz-transform: rotate(30deg); }
  11% { -moz-transform: rotate(-28deg); }
  13% { -moz-transform: rotate(26deg); }
  15% { -moz-transform: rotate(-24deg); }
  17% { -moz-transform: rotate(22deg); }
  19% { -moz-transform: rotate(-20deg); }
  21% { -moz-transform: rotate(18deg); }
  23% { -moz-transform: rotate(-16deg); }
  25% { -moz-transform: rotate(14deg); }
  27% { -moz-transform: rotate(-12deg); }
  29% { -moz-transform: rotate(10deg); }
  31% { -moz-transform: rotate(-8deg); }
  33% { -moz-transform: rotate(6deg); }
  35% { -moz-transform: rotate(-4deg); }
  37% { -moz-transform: rotate(2deg); }
  39% { -moz-transform: rotate(-1deg); }
  41% { -moz-transform: rotate(1deg); }

  43% { -moz-transform: rotate(0); }
  100% { -moz-transform: rotate(0); }
}

@keyframes ring {
  0% { transform: rotate(0); }
  1% { transform: rotate(30deg); }
  3% { transform: rotate(-28deg); }
  5% { transform: rotate(34deg); }
  7% { transform: rotate(-32deg); }
  9% { transform: rotate(30deg); }
  11% { transform: rotate(-28deg); }
  13% { transform: rotate(26deg); }
  15% { transform: rotate(-24deg); }
  17% { transform: rotate(22deg); }
  19% { transform: rotate(-20deg); }
  21% { transform: rotate(18deg); }
  23% { transform: rotate(-16deg); }
  25% { transform: rotate(14deg); }
  27% { transform: rotate(-12deg); }
  29% { transform: rotate(10deg); }
  31% { transform: rotate(-8deg); }
  33% { transform: rotate(6deg); }
  35% { transform: rotate(-4deg); }
  37% { transform: rotate(2deg); }
  39% { transform: rotate(-1deg); }
  41% { transform: rotate(1deg); }

  43% { transform: rotate(0); }
  100% { transform: rotate(0); }
}

.dropdown-animate-in {
  animation: dropdownSlideIn 0.25s ease-out forwards;
  transform-origin: top right;
}

.dropdown-animate-out {
  animation: dropdownSlideOut 0.2s ease-in forwards;
  transform-origin: top right;
}

@keyframes dropdownSlideIn {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes dropdownSlideOut {
  0% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  100% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
}

.notifications-animate-in {
  animation: notificationsSlideIn 0.3s ease-out forwards 0.1s;
  opacity: 0;
  transform: translateY(-5px);
}

@keyframes notificationsSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>